import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LegalInfoWithId, UploadedFile, FileWithUrl } from '../../types/forms';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  RadioGroup,
  FormLabel,
  Radio,
  Link,
  Alert,
  AlertTitle,
  Typography,
  Switch,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { uploadFile, deleteFile } from '../../services/uploadService';
import { refreshDocumentUrls } from '../../utils/api';
import api from '../../utils/api';
import { nationalities } from '../../data/nationalities';

interface LegalTabProps {
  formData: LegalInfoWithId;
  onChange: (field: keyof LegalInfoWithId, value: any) => void;
  onNext?: () => void;
  onBack?: () => void;
  onSave: () => void;
  saveStatus: 'saved' | 'error' | null;
  standalone?: boolean; // Flag to indicate if this is a standalone form
  token?: string; // Application token for public forms
}

type FormErrors = {
  [key: string]: string;
};

const LEGAL_FIELDS = [
  'dbsCheck',
  'dbsType',
  'dbsNumber',
  'dbsIssueDate', // Issue/print date from certificate
  'dbsCertificate',
  'dbsUpdateServiceId',
  'dbsUpdateServiceConsent',
  'legalFirstName', // Legal name on DBS certificate (if different from preferred name)
  'legalLastName', // Legal surname on DBS certificate (if different from preferred name)
  'nationality',
  'workPermit',
  'workPermitDetails',
  'workPermitExpiryDate',
  'workPermitNoExpiry',
  'insuranceCertificate',
  'insuranceNumber',
  'insuranceExpiryDate',
  'insuranceProvider',
  'insuranceCoverage',
  'insuranceDetails',
  'convictions',
  'convictionsDetails',
  'criminalInvestigation',
  'criminalInvestigationDetails',
  // Fields moved from education
  'bacpNumber',
  'supervisorName',
  'supervisorPhoneNumber',
  'onlineLearningCourse',
  'onlineLearningDetails',
  'onlineLearningCertificate'
] as const;

type LegalField = typeof LEGAL_FIELDS[number];

const LegalTab = forwardRef<HTMLDivElement, LegalTabProps>((props, ref) => {
  const { formData, onChange, onNext, onBack, onSave, saveStatus, token } = props;
  const params = useParams<{ id: string | undefined }>();
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const [, setUploadError] = useState<string | null>(null);
  const [dbsCheckLoading, setDbsCheckLoading] = useState(false);
  const [dbsCheckResult, setDbsCheckResult] = useState<any>(null);
  const [dbsCheckError, setDbsCheckError] = useState<string | null>(null);

  const refreshUrls = useCallback(async () => {
      if (!params.id) {
        console.error('Application ID not found');
        return;
      }

      const newUrls: { [key: string]: string } = {};
      const fields = ['dbsCertificate', 'insuranceCertificate'] as const;
      
      for (const field of fields) {
        const file = formData[field] as UploadedFile | null;
        if (file?.fileName) {
          try {
            // For public application forms, use the token from props or URL params
            // For authenticated routes, use the counsellor ID
            let counsellorId;
            if (token) {
              // If we have a token prop, use it (public application form)
              counsellorId = token;
            } else if (params.id && window.location.pathname.includes('/application-form/')) {
              // If we're in the application form context and have an ID in the URL, use it
              counsellorId = params.id;
            } else if (params.id) {
              // For authenticated routes with counsellor ID
              counsellorId = params.id;
            } else {
              console.error('No counsellor ID or token available for document refresh');
              continue;
            }
            console.log('Refreshing document URLs with ID:', counsellorId);
            const urls = await refreshDocumentUrls(counsellorId);
            const docUrl = urls[field];
            if (docUrl) {
              newUrls[field] = docUrl;
            }
          } catch (error) {
            console.error(`Error fetching ${field} URL:`, error);
          }
        }
      }
      
      // Handle onlineLearningCertificate URL refresh
      try {
        // For public application forms, use the token from props or URL params
        // For authenticated routes, use the counsellor ID
        let certCounsellorId;
        if (token) {
          // If we have a token prop, use it (public application form)
          certCounsellorId = token;
        } else if (params.id && window.location.pathname.includes('/application-form/')) {
          // If we're in the application form context and have an ID in the URL, use it
          certCounsellorId = params.id;
        } else if (params.id) {
          // For authenticated routes with counsellor ID
          certCounsellorId = params.id;
        } else {
          console.error('No counsellor ID or token available for document refresh');
          return;
        }
        
        const documentUrls = await refreshDocumentUrls(certCounsellorId);
        if (documentUrls.onlineLearningCertificate && formData.onlineLearningCertificate) {
          newUrls.onlineLearningCertificate = documentUrls.onlineLearningCertificate;
        }
      } catch (error) {
        console.error('Error fetching onlineLearningCertificate URL:', error);
      }
      
      setFileUrls(newUrls);
  }, [formData, params.id, token]);

  useEffect(() => {
    refreshUrls();
    const refreshInterval = setInterval(refreshUrls, 45 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [refreshUrls]);

  const validateField = (field: LegalField, value: any): string => {
    switch (field) {
      case 'dbsNumber':
        if (formData.dbsCheck && !value) {
          return 'DBS number is required';
        }
        return '';
      case 'dbsIssueDate':
        // Skip validation if DBS check is not selected
        if (!formData.dbsCheck) {
          return '';
        }
        
        // Skip validation if DBS type is not certificate
        if (formData.dbsType !== 'certificate') {
          return '';
        }
        
        // Check if value exists
        if (!value) {
          return 'DBS issue date is required when Certificate is selected';
        }
        
        // Check if the DBS issue date is valid
        const issueDate = dayjs(value);
        const today = dayjs().startOf('day');
        
        if (!issueDate.isValid()) {
          return 'Invalid date format';
        }
        
        if (issueDate.isAfter(today, 'day')) {
          return 'DBS issue date cannot be in the future';
        }
        
        return '';
      case 'dbsCertificate':
        if (formData.dbsCheck && formData.dbsType === 'certificate' && !value) {
          return 'DBS certificate is required';
        }
        return '';
      case 'dbsUpdateServiceId':
        if (formData.dbsCheck && formData.dbsType === 'update_service' && !value) {
          return 'DBS Update Service ID is required';
        }
        return '';
      case 'dbsUpdateServiceConsent':
        if (formData.dbsCheck && formData.dbsType === 'update_service' && !value) {
          return 'You must consent to DBS Update Service checks';
        }
        return '';
      case 'nationality':
        return !value ? 'Nationality is required' : '';
      case 'workPermitDetails':
        if (formData.workPermit && !value) {
          return 'Work permit details are required';
        }
        return '';
      case 'workPermitExpiryDate':
        if (formData.workPermit && !formData.workPermitNoExpiry && !value) {
          return 'Work permit expiry date is required unless the permit has no expiry date';
        }
        return '';
      case 'convictionsDetails':
        if (formData.convictions && !value) {
          return 'Conviction details are required';
        }
        return '';
      case 'criminalInvestigationDetails':
        if (formData.criminalInvestigation && !value) {
          return 'Criminal investigation details are required';
        }
        return '';
      // Fields moved from education
      case 'bacpNumber':
        return !value ? 'BACP / NCPS number is required' : '';
      case 'supervisorName':
        return !value ? 'Supervisor name is required' : '';
      case 'supervisorPhoneNumber':
        return !value ? 'Supervisor phone number is required' : '';
      case 'onlineLearningDetails':
        if (formData.onlineLearningCourse && !value) {
          return 'Online learning details are required';
        }
        return '';
      case 'onlineLearningCertificate':
        if (formData.onlineLearningCourse && !value) {
          return 'Online learning certificate is required';
        }
        return '';
      case 'insuranceProvider':
        return !value ? 'Insurance provider is required' : '';
      case 'insuranceNumber':
        return !value ? 'Insurance policy number is required' : '';
      case 'insuranceExpiryDate':
        if (!value) {
          return 'Insurance expiry date is required';
        }
        
        const insuranceExpiryDate = dayjs(value);
        const currentDate = dayjs().startOf('day');
        
        if (!insuranceExpiryDate.isValid()) {
          return 'Please enter a valid date';
        }
        
        if (insuranceExpiryDate.isBefore(currentDate, 'day')) {
          return 'Insurance expiry date must be today or in the future';
        }
        
        return '';
      case 'insuranceCertificate':
        return !value ? 'Insurance certificate is required' : '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Always validate these fields (DBS is now mandatory)
    const requiredFields = [
      'nationality', 
      'insuranceProvider', 
      'insuranceNumber', 
      'insuranceExpiryDate', 
      'insuranceCertificate',
      'bacpNumber',
      'supervisorName',
      'supervisorPhoneNumber'
    ];
    
    // DBS is mandatory - must have dbsCheck = true
    if (!formData.dbsCheck) {
      newErrors.dbsCheck = 'DBS check is mandatory for all counsellors';
      isValid = false;
    }
    
    // If DBS check is true, must select a type
    if (formData.dbsCheck && !formData.dbsType) {
      newErrors.dbsType = 'Please select DBS type (Certificate or Update Service)';
      isValid = false;
    }
    
    // Add conditional fields based on DBS type
    if (formData.dbsCheck) {
      requiredFields.push('dbsNumber');
      if (formData.dbsType === 'certificate') {
        // Always validate dbsIssueDate for certificate type
        requiredFields.push('dbsIssueDate');
        requiredFields.push('dbsCertificate');
      } else if (formData.dbsType === 'update_service') {
        requiredFields.push('dbsUpdateServiceId', 'dbsUpdateServiceConsent');
      }
    }

    // Add conditional fields based on work permit
    if (formData.workPermit) {
      requiredFields.push('workPermitDetails');
      if (!formData.workPermitNoExpiry) {
        requiredFields.push('workPermitExpiryDate');
      }
    }
    
    // Add conditional fields for convictions
    if (formData.convictions) {
      requiredFields.push('convictionsDetails');
    }
    
    // Add conditional fields for criminal investigation
    if (formData.criminalInvestigation) {
      requiredFields.push('criminalInvestigationDetails');
    }
    
    // Add conditional fields for online learning
    if (formData.onlineLearningCourse) {
      requiredFields.push('onlineLearningDetails', 'onlineLearningCertificate');
    }

    requiredFields.forEach(field => {
      const error = validateField(field as LegalField, formData[field as keyof LegalInfoWithId]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleFieldChange = (field: LegalField, value: any) => {
    const newValue = value === '' ? undefined : value;
    try {
      const error = validateField(field, newValue);
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
      onChange(field, newValue);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrors(prev => ({
        ...prev,
        [field]: errorMessage,
      }));
    }
  };

  const handleDateChange = (field: LegalField, value: dayjs.Dayjs | null) => {
    // Store as local "YYYY-MM-DD" — serialising a Date via toISOString()
    // shifts the day backwards in timezones ahead of UTC (e.g. BST).
    onChange(field, value ? value.format('YYYY-MM-DD') : undefined);
  };

  const handleFileUpload = async (field: LegalField, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    const allowedTypes = [
      'application/pdf', 
      'image/jpeg', 
      'image/png', 
      'image/gif',
      'application/msword',  
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'  
    ];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only PDF, Word documents (DOC, DOCX), and image files (JPEG, PNG, GIF) are allowed');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      
      // For public application forms, use the token from props or URL params
      // For authenticated routes, don't pass an applicationToken (will use cookies/session)
      let uploadOptions = {};
      if (token) {
        // If we have a token prop, use it (public application form)
        uploadOptions = { applicationToken: token };
      } else if (params.id && window.location.pathname.includes('/application-form/')) {
        // If we're in the application form context and have an ID in the URL, use it
        uploadOptions = { applicationToken: params.id };
      }
      // Otherwise, don't pass any token (will use authenticated session)
      
      console.log('Upload options:', uploadOptions);
      const result = await uploadFile(file, uploadOptions);
      console.log('Upload successful:', result);

      // Create the file structure for all uploads
      const fileWithUrl: FileWithUrl = {
        name: result.fileName,
        url: result.fileUrl,
        originalName: result.originalName
      };
      // Handle file upload for all fields uniformly
      onChange(field, fileWithUrl);
      setFileUrls(prev => ({
        ...prev,
        [field]: result.fileUrl
      }));
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (field: LegalField) => {
    const file = formData[field as keyof LegalInfoWithId] as FileWithUrl | null;
    if (!file?.name) return;

    try {
      await deleteFile(file.name);
      onChange(field, null);
      setFileUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[field];
        return newUrls;
      });
    } catch (error) {
      console.error('Delete error:', error);
      setUploadError('Failed to delete file. Please try again.');
    }
  };

  const handlePreviewFile = (file: FileWithUrl, field: LegalField) => {
    const uploadedFile: UploadedFile = {
      fileUrl: fileUrls[field] || file.url,
      fileName: file.name,
      originalName: file.name,
      size: 0, 
      mimetype: getMimeType(file.name) 
    };
    setPreviewFile(uploadedFile);
    setPreviewDialogOpen(true);
  };

  const getMimeType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'gif': return 'image/gif';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      default: return 'application/octet-stream';
    }
  };

  const isImageFile = (mimetype: string) => {
    return mimetype.startsWith('image/');
  };

  const renderFileUpload = (field: LegalField, label: string, description: string) => {
    const file = formData[field] as FileWithUrl | null;
    const fileUrl = fileUrls[field];
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{label}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{description}</Typography>
        
        {file ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {file.originalName || file.name}
            </Typography>
            
            {fileUrl && (
              <Tooltip title="Preview File">
                <IconButton
                  size="small"
                  onClick={() => handlePreviewFile(file, field)}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            )}
            
            <IconButton
              size="small"
              onClick={() => handleDeleteFile(field)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ) : (
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadIcon />}
            sx={{ mt: 1 }}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
            <input
              type="file"
              hidden
              onChange={(e) => handleFileUpload(field, e)}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
          </Button>
        )}
      </Box>
    );
  };

  const handleSave = () => {
    validateForm();
    onSave();
  };

  return (
    <div ref={ref}>
      <Box component="form" sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Nationality and Work Permit Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Nationality & Work Permit
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.nationality}>
                    <InputLabel>Nationality</InputLabel>
                    <Select
                      value={formData.nationality || ''}
                      onChange={(e) => handleFieldChange('nationality', e.target.value)}
                      label="Nationality"
                    >
                      {nationalities.map((nationality) => (
                        <MenuItem key={nationality} value={nationality}>
                          {nationality}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.nationality && (
                      <Typography color="error" variant="caption">
                        {errors.nationality}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.workPermit || false}
                        onChange={(e) => handleFieldChange('workPermit', e.target.checked)}
                      />
                    }
                    label="Do you require a work permit?"
                  />
                </Grid>
                {formData.workPermit && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Work Permit Details"
                        value={formData.workPermitDetails || ''}
                        onChange={(e) => handleFieldChange('workPermitDetails', e.target.value)}
                        error={!!errors.workPermitDetails}
                        helperText={errors.workPermitDetails || 'Please provide details of your work permit status'}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.workPermitNoExpiry || false}
                            onChange={(e) => handleFieldChange('workPermitNoExpiry', e.target.checked)}
                          />
                        }
                        label="No expiry date (indefinite right to work)"
                      />
                    </Grid>
                    {!formData.workPermitNoExpiry && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Work Permit Expiry Date"
                          value={formData.workPermitExpiryDate ? dayjs(formData.workPermitExpiryDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => handleFieldChange('workPermitExpiryDate', e.target.value ? new Date(e.target.value) : undefined)}
                          error={!!errors.workPermitExpiryDate}
                          helperText={errors.workPermitExpiryDate || 'Required unless your right to work is indefinite'}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Identity Documents Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Identity Documents
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>ID Verification:</strong> Your identity will be verified by our ID verification partner - TrustID.
                  </Alert>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* DBS Information Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                DBS (Disclosure and Barring Service)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.dbsCheck || false}
                        onChange={(e) => {
                          handleFieldChange('dbsCheck', e.target.checked);
                          if (!e.target.checked) {
                            handleFieldChange('dbsType', undefined);
                          }
                        }}
                      />
                    }
                    label="Do you have a DBS check?"
                  />
                </Grid>

                {formData.dbsCheck && (
                  <>
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">How would you like to verify your DBS status?</FormLabel>
                        <RadioGroup
                          value={formData.dbsType || ''}
                          onChange={(e) => handleFieldChange('dbsType', e.target.value)}
                        >
                          <FormControlLabel
                            value="certificate"
                            control={<Radio />}
                            label="I have a DBS certificate"
                          />
                          <FormControlLabel
                            value="update_service"
                            control={<Radio />}
                            label="I am registered with the DBS Update Service"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {formData.dbsType === 'certificate' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="DBS Number"
                            value={formData.dbsNumber}
                            onChange={(e) => handleFieldChange('dbsNumber', e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="DBS Issue Date"
                              value={formData.dbsIssueDate ? dayjs(formData.dbsIssueDate) : null}
                              onChange={(date) => handleDateChange('dbsIssueDate', date)}
                              format="DD/MM/YYYY"
                              maxDate={dayjs()}
                              shouldDisableDate={(date) => {
                                const today = dayjs();
                                // Disable future dates (dates after today)
                                return date.isAfter(today, 'day');
                              }}
                              slotProps={{
                                textField: {
                                  error: !!errors.dbsIssueDate,
                                  helperText: errors.dbsIssueDate || 'Enter the issue/print date from your DBS certificate. Expiry will be calculated automatically (3 years).',
                                  fullWidth: true
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12}>
                          {renderFileUpload(
                            'dbsCertificate',
                            'Upload DBS Certificate',
                            'Upload your current DBS certificate'
                          )}
                        </Grid>
                      </>
                    )}

                    {formData.dbsType === 'update_service' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="DBS Number"
                            value={formData.dbsNumber || ''}
                            onChange={(e) => handleFieldChange('dbsNumber', e.target.value)}
                            error={!!errors.dbsNumber}
                            helperText={errors.dbsNumber || 'Enter your DBS certificate number'}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="DBS Update Service ID"
                            value={formData.dbsUpdateServiceId || ''}
                            onChange={(e) => handleFieldChange('dbsUpdateServiceId', e.target.value)}
                            error={!!errors.dbsUpdateServiceId}
                            helperText={errors.dbsUpdateServiceId || 'Enter your DBS Update Service ID'}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            If the name on your DBS certificate differs from your preferred name, please enter your legal name as it appears on the certificate:
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Legal First Name (Optional)"
                            value={formData.legalFirstName || ''}
                            onChange={(e) => handleFieldChange('legalFirstName', e.target.value)}
                            helperText="Only if different from your preferred name"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Legal Last Name (Optional)"
                            value={formData.legalLastName || ''}
                            onChange={(e) => handleFieldChange('legalLastName', e.target.value)}
                            helperText="Only if different from your preferred name"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.dbsUpdateServiceConsent || false}
                                onChange={(e) => handleFieldChange('dbsUpdateServiceConsent', e.target.checked)}
                              />
                            }
                            label="I consent to my DBS Update Service record being checked"
                          />
                          {errors.dbsUpdateServiceConsent && (
                            <Typography color="error" variant="caption" display="block">
                              {errors.dbsUpdateServiceConsent}
                            </Typography>
                          )}
                        </Grid>
                        
                        {/* DBS Check Now Button */}
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            onClick={async () => {
                              // Validate required fields first
                              if (!formData.dbsNumber?.trim()) {
                                setDbsCheckError('Please enter your DBS Number first');
                                return;
                              }
                              if (!formData.dbsUpdateServiceId?.trim()) {
                                setDbsCheckError('Please enter your DBS Update Service ID first');
                                return;
                              }
                              if (!formData.dbsUpdateServiceConsent) {
                                setDbsCheckError('Please provide consent for DBS Update Service checks');
                                return;
                              }
                              
                              // Save the form first to ensure data is in the database
                              onSave();
                              
                              setDbsCheckLoading(true);
                              setDbsCheckError(null);
                              setDbsCheckResult(null);
                              
                              try {
                                const response = await api.post('/api/dbs/check-own', {});
                                setDbsCheckResult(response.data);
                              } catch (error: any) {
                                console.error('DBS check error:', error);
                                setDbsCheckError(error.response?.data?.message || 'Failed to check DBS Update Service. Please verify your details are correct.');
                              } finally {
                                setDbsCheckLoading(false);
                              }
                            }}
                            disabled={dbsCheckLoading || !formData.dbsNumber || !formData.dbsUpdateServiceId || !formData.dbsUpdateServiceConsent}
                            sx={{ mt: 2 }}
                          >
                            {dbsCheckLoading ? 'Checking...' : 'Check DBS Update Service Now'}
                          </Button>
                        </Grid>
                        
                        {/* DBS Check Result */}
                        {dbsCheckResult && (
                          <Grid item xs={12}>
                            <Alert severity="success" sx={{ mt: 2 }}>
                              <AlertTitle>✅ DBS Check Successful</AlertTitle>
                              <Typography variant="body2">
                                <strong>Status:</strong> {dbsCheckResult.status === 'BLANK_NO_NEW_INFO' ? 'Clear - No disclosures' : dbsCheckResult.statusDescription}
                              </Typography>
                              {dbsCheckResult.forename && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Name on Certificate:</strong> {dbsCheckResult.forename} {dbsCheckResult.surname}
                                </Typography>
                              )}
                              {dbsCheckResult.printDate && (
                                <Typography variant="body2">
                                  <strong>Certificate Date:</strong> {new Date(dbsCheckResult.printDate).toLocaleDateString('en-GB')}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Your DBS Update Service details have been verified successfully!
                              </Typography>
                            </Alert>
                          </Grid>
                        )}
                        
                        {/* DBS Check Error */}
                        {dbsCheckError && (
                          <Grid item xs={12}>
                            <Alert severity="error" sx={{ mt: 2 }}>
                              <AlertTitle>❌ Check Failed</AlertTitle>
                              <Typography variant="body2">
                                {dbsCheckError}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Please verify:
                              </Typography>
                              <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                                <li>Your DBS Number is correct</li>
                                <li>Your DBS Update Service ID is correct</li>
                                <li>Your date of birth matches your DBS certificate</li>
                                <li>The surname matches your DBS certificate (or enter your legal surname if different)</li>
                              </ul>
                            </Alert>
                          </Grid>
                        )}
                        
                        <Grid item xs={12}>
                          <Alert severity="info">
                            <AlertTitle>DBS Update Service Information</AlertTitle>
                            <Typography variant="body2">
                              By providing your DBS Update Service details, you authorise us to check your DBS status online. 
                              This allows us to verify your DBS certificate without requiring you to submit a physical copy.
                            </Typography>
                            <Link 
                              href="https://www.gov.uk/dbs-update-service" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ display: 'block', mt: 1 }}
                            >
                              Learn more about the DBS Update Service
                            </Link>
                          </Alert>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Professional Insurance Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Professional Insurance
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Insurance Provider"
                    value={formData.insuranceProvider || ''}
                    onChange={(e) => handleFieldChange('insuranceProvider', e.target.value)}
                    error={!!errors.insuranceProvider}
                    helperText={errors.insuranceProvider || 'Enter your insurance provider name'}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Insurance Policy Number"
                    value={formData.insuranceNumber || ''}
                    onChange={(e) => handleFieldChange('insuranceNumber', e.target.value)}
                    error={!!errors.insuranceNumber}
                    helperText={errors.insuranceNumber || 'Enter your policy number'}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Insurance Expiry Date"
                      value={formData.insuranceExpiryDate ? dayjs(formData.insuranceExpiryDate) : null}
                      onChange={(date) => handleDateChange('insuranceExpiryDate', date)}
                      slotProps={{
                        textField: {
                          error: !!errors.insuranceExpiryDate,
                          helperText: errors.insuranceExpiryDate || 'Select when your insurance expires',
                          fullWidth: true,
                          required: true
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                  {renderFileUpload(
                    'insuranceCertificate',
                    'Professional Insurance Certificate',
                    'Upload your current professional indemnity insurance certificate'
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Criminal Convictions Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Criminal Convictions
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Do you have any criminal convictions?</InputLabel>
                    <Select
                      value={formData.convictions}
                      onChange={(e) => handleFieldChange('convictions', e.target.value === 'true')}
                      label="Do you have any criminal convictions?"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.convictions === true && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Convictions Details"
                      multiline
                      rows={4}
                      value={formData.convictionsDetails || ''}
                      onChange={(e) => handleFieldChange('convictionsDetails', e.target.value)}
                      error={!!errors.convictionsDetails}
                      helperText={errors.convictionsDetails || 'Please provide details about your convictions'}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          {/* Professional Credentials Section - Moved from Education Tab */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Professional Credentials
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="BACP / NCPS Number"
                    value={formData.bacpNumber || ''}
                    onChange={(e) => handleFieldChange('bacpNumber', e.target.value)}
                    error={!!errors.bacpNumber}
                    helperText={errors.bacpNumber || 'Enter your BACP or NCPS number'}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Supervisor Name"
                    value={formData.supervisorName || ''}
                    onChange={(e) => handleFieldChange('supervisorName', e.target.value)}
                    error={!!errors.supervisorName}
                    helperText={errors.supervisorName || 'Enter your supervisor\'s name'}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Supervisor Phone Number"
                    value={formData.supervisorPhoneNumber || ''}
                    onChange={(e) => handleFieldChange('supervisorPhoneNumber', e.target.value)}
                    error={!!errors.supervisorPhoneNumber}
                    helperText={errors.supervisorPhoneNumber || 'Enter your supervisor\'s phone number'}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Online Learning Section - Moved from Education Tab */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Online Learning
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.onlineLearningCourse || false}
                        onChange={(e) => handleFieldChange('onlineLearningCourse', e.target.checked)}
                      />
                    }
                    label="I have completed an online learning telephone course"
                  />
                </Grid>
                
                {formData.onlineLearningCourse && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Online Learning Telephone Course Details"
                        value={formData.onlineLearningDetails || ''}
                        onChange={(e) => handleFieldChange('onlineLearningDetails', e.target.value)}
                        multiline
                        rows={4}
                        error={!!errors.onlineLearningDetails}
                        helperText={errors.onlineLearningDetails || 'Please provide details about your online learning telephone course.'}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      {renderFileUpload(
                        'onlineLearningCertificate',
                        'Online Learning Course Certificate',
                        'Please upload your Online Learning Course Certificate (PDF, JPG, JPEG, or PNG format)'
                      )}
                      {errors.onlineLearningCertificate && (
                        <FormHelperText error>{errors.onlineLearningCertificate}</FormHelperText>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              {onBack && (
                <Button onClick={onBack} variant="outlined">
                  Back
                </Button>
              )}
              {!onBack && props.standalone && (
                <div></div> // Empty div for spacing when in standalone mode
              )}
              {/* Only show Save Progress button when not in standalone mode (i.e., in application form) */}
              {!props.standalone && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                >
                  Save Progress
                </Button>
              )}
              {onNext && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onNext}
                >
                  Next
                </Button>
              )}
              {!onNext && props.standalone && (
                <div></div> // Empty div for spacing when in standalone mode
              )}
            </Box>
          </Grid>

          {saveStatus === 'saved' && (
            <Grid item xs={12}>
              <Alert severity="success">Progress saved successfully!</Alert>
            </Grid>
          )}
          
          {saveStatus === 'error' && (
            <Grid item xs={12}>
              <Alert severity="error">Error saving progress. Please try again.</Alert>
            </Grid>
          )}
        </Grid>

        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Document Preview
            <IconButton
              onClick={() => setPreviewDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewFile && isImageFile(previewFile.mimetype) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <img
                  src={`/api/files/${previewFile.fileUrl}`}
                  alt="Document preview"
                  style={{ maxWidth: '100%', maxHeight: '70vh' }}
                />
              </Box>
            )}
            {previewFile && !isImageFile(previewFile.mimetype) && (
              <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
                Preview not available for this file type. Please download to view.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            {previewFile && (
              <Button
                href={`/api/files/${previewFile.fileUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
});

LegalTab.displayName = 'LegalTab';

export default LegalTab;